import * as React from 'react';
import { useEffect, useState } from 'react';

import { defaultWrapLinesPreference, mediumPageSizePreference, paginationLabels } from 'src/commons/tables';

import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  Table,
  TableProps,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { listTemplates } from 'src/api/resourcesmanager';

import { RMPageHeader } from '../components';
import { Page } from 'src/routes';
import { UnauthorizedModal } from '../components-V3';
import { EmptyState } from 'src/commons/EmptyState';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface MyTemplatesProps extends CommonPageProps {
  userInfo: any;
}

export const MyTemplates = (props: MyTemplatesProps) => {
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [tableMessage, setTableMessage] = useState('No templates');
  const [onboardModal, setOnboardModal] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
  });

  const ctColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'templateName',
      header: 'Name',
      cell: (item) => <Link href={Page.RG_TEMPLATE_DETAILS.replace(':id', item.id)}>{item.name}</Link>,
      minWidth: 200,
      sortingField: 'name',
    },
    {
      id: 'configTemplatDescription',
      header: 'Description',
      cell: (item) => item.description,
      minWidth: 300,
    },
  ];

  useEffect(() => {
    handleRefresh();
  }, []);

  const listCTs = async () =>
    listTemplates({
      groupId: props.activeGroup,
    });

  const fetchCTs = async () => {
    const { templates } = await listCTs();
    setTemplates(templates);
    setLoadingTemplates(false);
  };

  const handleRefresh = async () => {
    if (!props.activeGroup) return;
    setLoadingTemplates(true);
    try {
      fetchCTs();
    } catch (err) {
      if (err.name == 'InsufficientPermissionException') {
        setOnboardModal(true);
      }
      setTableMessage(`Unable to load templates. ${err.message}`);
    }
    setLoadingTemplates(false);
  };

  const handleAction = async (e) => {
    switch (e.detail.id) {
      case 'details':
        props.setRedirect(Page.RG_TEMPLATE_DETAILS.replace(':id', collectionProps.selectedItems?.[0].id));
        break;

      case 'create':
        props.setRedirect(Page.CREATE_RESOURCEGROUP, false, {
          values: JSON.parse(
            templates.filter((t) => t.id == collectionProps.selectedItems?.[0].id)[0].templateJsonString,
          ),
        });
        break;

      case 'edit':
        props.setRedirect(Page.UPDATE_RG_TEMPLATE.replace(':id', collectionProps.selectedItems?.[0].id));
        break;
    }
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(templates, {
    filtering: {
      empty: <EmptyState title={tableMessage} subtitle={'No templates to display.'} />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'name',
        },
      },
    },
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  return (
    <>
      <UnauthorizedModal visible={onboardModal} onClose={() => setOnboardModal(false)} />

      <Table
        {...collectionProps}
        loadingText='Loading templates...'
        loading={loadingTemplates}
        columnDefinitions={ctColumnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        empty={<EmptyState title={'No templates'} subtitle={'No templates to display'} />}
        header={
          <>
            <RMPageHeader
              buttons={[
                {
                  text: '',
                  icon: 'refresh',
                  onItemClick: fetchCTs,
                },
                {
                  text: 'Actions',
                  onItemClick: handleAction,
                  items: [
                    {
                      text: 'Quick create',
                      id: 'create',
                      disabled: !collectionProps.selectedItems.length,
                    },
                    {
                      text: 'View details',
                      id: 'details',
                      disabled: !collectionProps.selectedItems.length,
                    },
                    {
                      text: 'Edit',
                      id: 'edit',
                      disabled: !collectionProps.selectedItems.length,
                    },
                  ],
                  loading: loadingTemplates,
                },
                {
                  //Using 'New Template' to differentiate from 'Quick Create' (Resource Group)
                  text: 'New template',
                  onItemClick: () => {
                    props.setRedirect(Page.CREATE_RG_TEMPLATE);
                  },
                },
              ]}
              counter={!loadingTemplates && `(${items.length})`}
              subheader={'Templates'}
            />
          </>
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={mediumPageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel='Filter templates'
            filteringPlaceholder='Find templates'
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        selectionType={'single'}
      />
    </>
  );
};
